import { Button } from '@ibsys/kit';
import { ExternalLinkIcon, LinkIcon, PaperclipIcon } from 'lucide-react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { useLazyConversationAttachmentSignedUrl } from '../../model/api/hooks';
import type { ConversationAttachmentResponse, PhraseAttachmentInPhraseResponse } from '../../model/api/types';
import { AttachmentLink } from './AttachmentLink';

/**
 * Пропсы компонента {@link AttachmentItem}.
 */
type Props = {
  /** Вложение из ответа GET /conversation/{id}.attachments или из фразы транскрипта */
  attachment: ConversationAttachmentResponse | PhraseAttachmentInPhraseResponse;
  /** ID фразы вложения; используется для прокрутки к фразе из таба «Вложения». */
  phraseId?: number | null;
  /** Готовая подпись таймстемпа фразы (формат `M:SS - M:SS`); если null — кнопка не показывается. */
  phraseTimeLabel?: string | null;
  /** Колбэк прокрутки транскрипта к фразе вложения. Если не передан — кнопка не показывается. */
  onScrollToPhrase?: (phraseId: number) => void;
  /** Компактный режим (используется в облаке транскрипта) — без бордера и фоновой плашки. */
  compact?: boolean;
  className?: string;
};

/**
 * Универсальное отображение вложения диалога.
 *
 * Используется в табе «Вложения» и под склеенным облаком фраз транскрипта.
 * Длинная ссылка выводится в сокращённом виде (без схемы) и обрезается с
 * многоточием по доступной ширине; полный URL — в title и href/обработчике.
 *
 * Для внешних ссылок открывается напрямую в новой вкладке. Для внутренних —
 * выполняется запрос подписанной ссылки и затем открывается результат.
 */
export const AttachmentItem: FC<Props> = ({
  attachment,
  phraseId,
  phraseTimeLabel,
  onScrollToPhrase,
  compact = false,
  className,
}) => {
  const { t } = useTranslation('dialogs');
  const { refetch: fetchSignedUrl } = useLazyConversationAttachmentSignedUrl();
  const [isLoading, setIsLoading] = useState(false);

  // phraseId либо берётся из вложения карточки диалога, либо передаётся пропом из транскрипта
  const effectivePhraseId =
    phraseId ?? ('phraseId' in attachment ? (attachment.phraseId ?? null) : null);
  const hasUrl = !!attachment.url;
  const showScrollButton = onScrollToPhrase != null && effectivePhraseId != null && phraseTimeLabel != null;

  const handleOpenInternal = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const result = await fetchSignedUrl(attachment.id);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchSignedUrl, attachment.id, isLoading]);

  const handleScrollClick = useCallback(() => {
    if (effectivePhraseId != null) {
      onScrollToPhrase?.(effectivePhraseId);
    }
  }, [onScrollToPhrase, effectivePhraseId]);

  return (
    <div
      className={cn(
        'flex w-full min-w-0 items-center gap-3',
        !compact && 'rounded-md border border-border bg-card p-3',
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
          compact ? 'size-6' : 'size-8',
        )}
      >
        {attachment.isExternalUrl ? (
          <LinkIcon className={compact ? 'size-3' : 'size-4'} />
        ) : (
          <PaperclipIcon className={compact ? 'size-3' : 'size-4'} />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <AttachmentLink
          attachment={attachment}
          isLoading={isLoading}
          onOpenInternal={handleOpenInternal}
          compact={compact}
        />
        {!hasUrl && <span className="text-xs text-muted-foreground">{t('attachmentUnavailable')}</span>}
      </div>
      {attachment.isExternalUrl && !compact && (
        <span className="hidden shrink-0 items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground sm:inline-flex">
          <ExternalLinkIcon className="size-3" />
          {t('attachmentExternal')}
        </span>
      )}
      {showScrollButton && (
        <Button
          variant="ghost"
          size="sm"
          className={cn('shrink-0', compact ? 'text-current' : 'text-primary')}
          onClick={handleScrollClick}
          aria-label={t('attachmentScrollToPhrase')}
        >
          {`[${phraseTimeLabel}]`}
        </Button>
      )}
    </div>
  );
};

